import { DefaultButton, Dialog, DialogFooter, DialogType, Label, MessageBar, MessageBarType, PrimaryButton, Stack, TextField } from "@fluentui/react"
import { useState } from "react"
import { addQualifyingEvent, getCurrentUserEmail, maskAsDate, safeParseDateTime } from "../lib/helper"
import { makeCRMApiCall } from "../services/apiService"
import { IEnrollmentRecord } from "../types/IEnrollmentRecord"

interface UpdateStartDateDialogProps {
	closePanel: (refreshItems: boolean, success: boolean) => void
	employeeHouseholdMember: IEnrollmentRecord
	householdMembers: IEnrollmentRecord[]
}

export function UpdateStartDateDialog({ closePanel, employeeHouseholdMember, householdMembers }: UpdateStartDateDialogProps) {
	const [updating, setUpdating] = useState(false)
	const [startDate, setStartDate] = useState<Date | undefined>(undefined)
	const [dateDisplay, setDateDisplay] = useState<string>("")
	const [error, setError] = useState<string | undefined>(undefined)

	const householdMembersWithoutEmployee = householdMembers.filter(h => h.cw_householdmembersid !== employeeHouseholdMember.cw_householdmembersid)

	const [dateError, setDateError] = useState<string | undefined>()
	const onDateChange = (newValue?: string) => {
		const masked = maskAsDate(newValue || "")
		setDateDisplay(masked)

		setDateError(undefined)

		if (masked.length === 10) {
			const parsed = safeParseDateTime(masked)

			if (parsed?.isValid) {
				setStartDate(parsed.toJSDate())
			} else {
				setDateError("Please enter a valid date.")
			}
		}
	}

	return (
		<Dialog
			onDismiss={() => closePanel(false, false)}
			hidden={false}
			dialogContentProps={{
				type: DialogType.normal,
				title: (
					<Stack horizontalAlign="center">
						<h3 style={{ marginTop: 0 }}>Update Employee Coverage Start Date</h3>
					</Stack>
				)
			}}
			modalProps={{
				isBlocking: true
			}}>
			<Stack tokens={{ childrenGap: 8 }}>
				<div style={{ fontSize: 18, fontWeight: 500 }}>Set the insurance start date for the following:</div>
				<Stack tokens={{ childrenGap: 8 }}>
					<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
						<Label>Name</Label>
						<span>
							{employeeHouseholdMember.firstName} {employeeHouseholdMember.lastName}
						</span>
					</Stack>
					{householdMembers.length > 1 && (
						<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="start">
							<Label>Household Members</Label>
							<span>{householdMembersWithoutEmployee.map(h => `${h.firstName} ${h.lastName}`).join(", ")}</span>
						</Stack>
					)}
					<TextField
						label="Insurance Start Date"
						placeholder="MM/DD/YYYY"
						inputMode="numeric"
						value={dateDisplay}
						onChange={(_, newValue) => {
							onDateChange(newValue)
						}}
						maxLength={10}
						errorMessage={dateError}
					/>
				</Stack>
			</Stack>
			<DialogFooter>
				<Stack>
					<Stack horizontalAlign="center" horizontal tokens={{ childrenGap: 12 }}>
						<PrimaryButton
							disabled={updating}
							onClick={async () => {
								setUpdating(true)
								const body = {
									cw_insurancestartdate: startDate?.toISOString().split("T")[0]
								}
								const contactEmail = await getCurrentUserEmail()
								try {
									await Promise.all(householdMembers.map(h => makeCRMApiCall(`cw_householdmemberses(${h.cw_householdmembersid})`, "PATCH", JSON.stringify(body))))
									await Promise.all(
										householdMembers.map(h => {
											return addQualifyingEvent(
												contactEmail,
												`Updated insurance start date for ${h.firstName} ${h.lastName}: ${startDate?.toLocaleDateString("en-US")}`,
												h.employee_id
											)
										})
									)
									closePanel(true, true)
								} catch (err) {
									console.error(err)
									setError("An error occurred while updating the insurance start date. Please try again.")
								}
							}}
							text="Update"
						/>
						<DefaultButton onClick={() => closePanel(false, false)} text="Cancel" />
					</Stack>
				</Stack>
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			</DialogFooter>
		</Dialog>
	)
}
